import { useState } from 'react';

import type { CreateTimeEntryInput, PlainDate, TimeEntryDto, UserDto } from '@botguide/shared';
import { addDays, currentWeekStart, formatCents, formatWeekLabel } from '@botguide/shared';

import {
  Badge,
  Button,
  Card,
  CardHeader,
  Dialog,
  ErrorNote,
  ErrorState,
  Icon,
  LoadingScreen,
  PageHeader,
  SkeletonRows,
  Stat,
} from '@/components';
import { TimeEntryForm } from '@/components/hours/TimeEntryForm';
import { TimesheetTable, earnedState } from '@/components/hours/TimesheetTable';
import { formatDayShort, formatHours } from '@/lib/format';
import {
  useCreateTimeEntry,
  useDeleteTimeEntry,
  useTimeEntries,
  useUpdateTimeEntry,
} from '@/lib/queries';
import { useCan, useSession } from '@/lib/session';

/*
 * ---------------------------------------------------------------------------
 * My hours.
 * ---------------------------------------------------------------------------
 *
 * One week at a time, navigated by Monday. Every date on this screen comes out
 * of `@botguide/shared` - `currentWeekStart`, `addDays`, `formatWeekLabel` -
 * because a Monday computed locally is wrong twice a year and the API rejects
 * a `weekStart` that is not one.
 *
 * The screen is scoped to the signed-in person explicitly, with `userId`. A
 * contractor's scope already does that server-side; an owner's does not, and
 * "My hours" showing everybody's hours would be a surprising screen.
 */

export default function MyHours() {
  const { user } = useSession();
  if (!user) return <LoadingScreen label="Checking your session" />;
  return <Timesheet user={user} />;
}

/* -------------------------------------------------------------------------- */

function Timesheet({ user }: { user: UserDto }) {
  const [weekStart, setWeekStart] = useState<PlainDate>(() => currentWeekStart());
  const [editing, setEditing] = useState<TimeEntryDto | null>(null);
  const [deleting, setDeleting] = useState<TimeEntryDto | null>(null);
  /** Bumped after a successful create so the form remounts empty. */
  const [formKey, setFormKey] = useState(0);

  const entries = useTimeEntries({ weekStart, userId: user.id });
  const create = useCreateTimeEntry();
  const update = useUpdateTimeEntry();
  const remove = useDeleteTimeEntry();

  const canCreate = useCan('create', 'timeEntry');
  const canUpdate = useCan('update', 'timeEntry');
  const canDelete = useCan('delete', 'timeEntry');

  const thisWeek = currentWeekStart();
  const rows = entries.data ?? [];
  const totals = weekTotals(rows);

  function submitNew(values: CreateTimeEntryInput) {
    create.mutate(values, {
      onSuccess: () => setFormKey((key) => key + 1),
    });
  }

  function submitEdit(values: CreateTimeEntryInput) {
    if (!editing) return;
    update.mutate(
      { id: editing.id, patch: values },
      { onSuccess: () => setEditing(null) },
    );
  }

  function confirmDelete() {
    if (!deleting) return;
    remove.mutate(deleting.id, { onSuccess: () => setDeleting(null) });
  }

  return (
    <>
      <PageHeader
        title="My hours"
        description="Log time against a task in quarter-hour steps. Hours stay editable until they are reviewed."
        toolbar={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              iconLeft="chevronLeft"
              aria-label="Previous week"
              onClick={() => setWeekStart((week) => addDays(week, -7))}
            />
            <span className="rounded-md border border-line bg-surface px-3 py-1.5 font-mono text-[0.8125rem] tnum">
              {formatWeekLabel(weekStart)} – {formatDayShort(addDays(weekStart, 6))}
            </span>
            <Button
              size="sm"
              variant="secondary"
              iconRight="chevronRight"
              aria-label="Next week"
              onClick={() => setWeekStart((week) => addDays(week, 7))}
            />
            {weekStart === thisWeek ? (
              <Badge tone="accent" size="sm">
                This week
              </Badge>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => setWeekStart(thisWeek)}>
                Back to this week
              </Button>
            )}
          </div>
        }
      />

      <Card padding="lg" className="rise mb-6">
        <div className="grid grid-cols-2 gap-x-6 gap-y-6 lg:grid-cols-5">
          <Stat label="Logged" value={formatHours(totals.loggedMinutes)} hint={`${totals.count} entries`} />
          <Stat
            label="Pending"
            value={formatHours(totals.pendingMinutes)}
            hint="awaiting review"
            tone="var(--warn)"
          />
          <Stat
            label="Approved"
            value={formatHours(totals.approvedMinutes)}
            hint="signed off"
            tone="var(--ok)"
          />
          <Stat
            label="Rejected"
            value={formatHours(totals.rejectedMinutes)}
            hint="not billable"
            tone={totals.rejectedMinutes > 0 ? 'var(--danger)' : undefined}
          />
          <Stat
            label="Earned"
            value={formatCents(totals.earnedCents)}
            hint="on approved hours"
            tone="var(--accent)"
          />
        </div>

        {totals.unpricedApprovals > 0 ? (
          <p className="mt-5 flex items-start gap-2 border-t border-line pt-4 text-[0.8125rem] text-warn-ink">
            <Icon name="alert" size={14} className="mt-0.5 shrink-0" />
            <span>
              {totals.unpricedApprovals} approved{' '}
              {totals.unpricedApprovals === 1 ? 'entry has' : 'entries have'} no pay rate on file for
              the day worked, so {totals.unpricedApprovals === 1 ? 'it is' : 'they are'} not counted
              in the total above. Ask Ryan to set a rate.
            </span>
          </p>
        ) : null}
      </Card>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_21rem]">
        {canCreate ? (
          <aside className="xl:order-2">
            <Card padding="lg" className="rise xl:sticky xl:top-6" style={{ animationDelay: '70ms' }}>
              <CardHeader
                title="Log time"
                description={`Into the week of ${formatWeekLabel(weekStart)}`}
              />
              <div className="mt-4">
                <TimeEntryForm
                  key={formKey}
                  userId={user.id}
                  weekStart={weekStart}
                  busy={create.isPending}
                  error={create.error}
                  onSubmit={submitNew}
                />
              </div>
            </Card>
          </aside>
        ) : null}

        <div className="min-w-0 xl:order-1">
          {entries.isPending ? (
            <SkeletonRows rows={6} />
          ) : entries.isError ? (
            <ErrorState error={entries.error} onRetry={() => void entries.refetch()} />
          ) : (
            <TimesheetTable
              entries={rows}
              canEdit={canUpdate}
              canDelete={canDelete}
              busyId={remove.isPending ? deleting?.id : null}
              onEdit={(entry) => {
                update.reset();
                setEditing(entry);
              }}
              onDelete={(entry) => {
                remove.reset();
                setDeleting(entry);
              }}
            />
          )}
        </div>
      </div>

      <Dialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        title="Edit these hours"
        description="Only hours that are still pending can be changed."
      >
        {editing ? (
          <TimeEntryForm
            userId={user.id}
            weekStart={weekStart}
            entry={editing}
            submitLabel="Save changes"
            busy={update.isPending}
            error={update.error}
            onSubmit={submitEdit}
            onCancel={() => setEditing(null)}
          />
        ) : null}
      </Dialog>

      <Dialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        size="sm"
        title="Delete these hours?"
        description="This cannot be undone."
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleting(null)} disabled={remove.isPending}>
              Keep them
            </Button>
            <Button variant="danger" loading={remove.isPending} onClick={confirmDelete}>
              Delete
            </Button>
          </>
        }
      >
        {deleting ? (
          <div className="text-sm">
            <p className="font-medium">{deleting.taskTitle}</p>
            <p className="mt-1 text-muted">
              {formatDayShort(deleting.workDate)} · {formatHours(deleting.minutes)} ·{' '}
              {deleting.projectName}
            </p>
            {remove.error ? <ErrorNote className="mt-3" error={remove.error} /> : null}
          </div>
        ) : null}
      </Dialog>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Totals                                                                     */
/* -------------------------------------------------------------------------- */

interface WeekTotals {
  count: number;
  loggedMinutes: number;
  pendingMinutes: number;
  approvedMinutes: number;
  rejectedMinutes: number;
  earnedCents: number;
  /** Approved entries the viewer may price, but for which no rate exists. */
  unpricedApprovals: number;
}

/**
 * The week in five figures.
 *
 * Earnings are summed through `earnedState`, which is the one place that knows
 * an absent `earnedCents` means "you may not see this" while a null one means
 * "there is no rate on file". Adding the field naively would treat both as
 * zero and quietly under-report somebody's pay.
 */
function weekTotals(entries: readonly TimeEntryDto[]): WeekTotals {
  const totals: WeekTotals = {
    count: entries.length,
    loggedMinutes: 0,
    pendingMinutes: 0,
    approvedMinutes: 0,
    rejectedMinutes: 0,
    earnedCents: 0,
    unpricedApprovals: 0,
  };

  for (const entry of entries) {
    totals.loggedMinutes += entry.minutes;
    if (entry.status === 'pending') totals.pendingMinutes += entry.minutes;
    if (entry.status === 'approved') totals.approvedMinutes += entry.minutes;
    if (entry.status === 'rejected') totals.rejectedMinutes += entry.minutes;

    const earned = earnedState(entry);
    if (earned.kind === 'earned') totals.earnedCents += earned.cents;
    if (earned.kind === 'no-rate') totals.unpricedApprovals += 1;
  }

  return totals;
}
